import React, { useEffect, useState } from "react";
import { Payment } from "square";
import PaymentInputGroup from "./PaymentInputGroup";
import PaymentSelectGroup from "./PaymentSelectGroup";
import AddressSearchInput from "./AddressSearchInput";
import StyledCheckbox from "./StyledCheckbox";
import { getGeoapifyKey } from "../api";
import { GeoapifyProperties } from "../types/AddressSearchItem";
import { isValidPhoneNumber } from "../extensions/helpers";

const DEFAULT_COUNTRY: string = "US";

// Geoapify returns the full state name (e.g. "New Jersey") in its properties.
// Printful and Square both require the 2-letter abbreviation (e.g. "NJ").
const STATE_ABBREVIATIONS: Record<string, string> = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
  "District of Columbia": "DC",
};

/** Returns the 2-letter state abbreviation whether given a full name or already an abbreviation. */
const toStateAbbreviation = (state?: string): string => {
  if (!state) return "";
  if (state.length === 2) return state.toUpperCase();
  return STATE_ABBREVIATIONS[state] ?? state;
};

type PaymentContactInfoProps = {
  details: Payment;
  setDetails: React.Dispatch<React.SetStateAction<Payment>>;
  phone: string;
  setPhone: React.Dispatch<React.SetStateAction<string>>;
};

const PaymentContactInfo = ({
  details,
  setDetails,
  phone,
  setPhone,
}: PaymentContactInfoProps) => {
  const [geoapifyKey, setGeoapifyKey] = useState<string | null>(null);

  const [sameAsShipping, setSameAsShipping] = useState<boolean>(true);

  // Only complain about the phone once they've typed something — an untouched
  // empty field shouldn't shout at someone who just opened checkout.
  const [phoneTouched, setPhoneTouched] = useState<boolean>(false);
  const phoneError =
    phoneTouched && phone.trim().length > 0 && !isValidPhoneNumber(phone)
      ? "Enter a valid 10-digit US phone number."
      : null;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setDetails((prev) => ({
      ...prev,
      buyerEmailAddress: value,
    }));
  };

  const handleShippingChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setDetails((prev) => {
      const newShipping = {
        ...prev.shippingAddress,
        [name]: value,
        country: DEFAULT_COUNTRY,
      };
      return {
        ...prev,
        shippingAddress: newShipping,
        // Keep billing in sync whenever "Same as Shipping" is checked so card
        // payments don't submit a stale/incomplete billing address.
        ...(sameAsShipping && { billingAddress: newShipping }),
      };
    });
  };

  const handleBillingChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setDetails((prev) => ({
      ...prev,
      billingAddress: {
        ...prev.billingAddress,
        [name]: value,
        country: DEFAULT_COUNTRY,
      },
    }));
  };

  const handleSelectShippingAddressSearchResult = (
    searchResult: GeoapifyProperties,
  ) => {
    setDetails((prev) => {
      const newShipping = {
        ...prev.shippingAddress,
        addressLine1: searchResult.address_line1,
        locality: searchResult.city,
        administrativeDistrictLevel1: toStateAbbreviation(searchResult.state),
        postalCode: searchResult.postcode?.slice(0, 5),
        country: "US",
      };
      return {
        ...prev,
        shippingAddress: newShipping,
        ...(sameAsShipping && { billingAddress: newShipping }),
      };
    });
  };

  const handleSelectBillingAddressSearchResult = (
    searchResult: GeoapifyProperties,
  ) => {
    setDetails((prev) => ({
      ...prev,
      billingAddress: {
        ...prev.billingAddress,
        addressLine1: searchResult.address_line1,
        locality: searchResult.city,
        administrativeDistrictLevel1: toStateAbbreviation(searchResult.state),
        postalCode: searchResult.postcode?.slice(0, 5),
        country: "US",
      },
    }));
  };

  const handleSameAsShippingChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSameAsShipping(e.currentTarget.checked);
    if (e.currentTarget.checked) {
      setDetails((prev) => ({
        ...prev,
        billingAddress: prev.shippingAddress,
      }));
    } else {
      // Clear the copied address but carry the name over as a starting value —
      // most people uncheck because the *address* differs, not the name. Anyone
      // shipping a gift overwrites it, which is what lets the confirmation email
      // greet the buyer instead of the recipient (see send-email-order.js).
      setDetails((prev) => ({
        ...prev,
        billingAddress: {
          firstName: prev.shippingAddress?.firstName,
          lastName: prev.shippingAddress?.lastName,
          country: DEFAULT_COUNTRY,
        },
      }));
    }
  };

  useEffect(() => {
    const getKey = async () => {
      const keyResponse = await getGeoapifyKey();
      if (keyResponse?.data && !keyResponse.error) {
        setGeoapifyKey(keyResponse.data.geoapifyKey);
      } else {
        console.error("Error fetching key.", keyResponse?.error);
      }
    };

    getKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* The BUYER's email, and the only field here that's theirs no matter
          what: it receives every lifecycle email and is the ownership key that
          gates guest order lookup (`get-order`). Hence it sits above both
          address blocks rather than inside either one. */}
      <PaymentInputGroup
        type="email"
        name="email"
        placeholder="Email"
        value={details?.buyerEmailAddress || ""}
        onChange={handleEmailChange}
        toolTipText="Required — we send your order confirmation, shipping updates and tracking here, and it's how you look up your order later."
        required
      />

      {/* Shipping Address */}
      <h2 className="text-lg font-bold mt-6 mb-2">Shipping Address</h2>

      {/* The RECIPIENT's name — it's what Printful prints on the label
          (create-printful-order builds `recipient.name` from shippingAddress).
          It lives UNDER this heading, not up with the phone/email, because the
          contact block is headed "Contact Information" and a gift buyer reading
          a bare name field there types their OWN name — which would ship the
          parcel addressed to the buyer at the recipient's address.

          handleShippingChange mirrors it into billing while "Same as Shipping"
          is checked; once that's unchecked the billing section owns its own
          name, so this must not write there or it would overwrite the buyer's
          name on every keystroke. */}
      <div className="flex gap-4">
        <PaymentInputGroup
          placeholder="First Name"
          type="text"
          name="firstName"
          value={details?.shippingAddress?.firstName || ""}
          onChange={handleShippingChange}
          toolTipText="Who the parcel is addressed to — this goes on the shipping label."
          required
        />
        <PaymentInputGroup
          placeholder="Last Name"
          type="text"
          name="lastName"
          value={details?.shippingAddress?.lastName || ""}
          onChange={handleShippingChange}
          required
        />
      </div>

      <AddressSearchInput
        name="addressLine1"
        placeholder="Street Address"
        value={details?.shippingAddress?.addressLine1 || ""}
        onChange={handleShippingChange}
        onSelect={handleSelectShippingAddressSearchResult}
        geoapifyKey={geoapifyKey}
        required
      />
      <PaymentInputGroup
        type="text"
        name="addressLine2"
        placeholder="Address Line 2"
        value={details?.shippingAddress?.addressLine2 || ""}
        onChange={handleShippingChange}
        hideLabel
      />
      <PaymentInputGroup
        type="text"
        name="addressLine3"
        placeholder="Address Line 3"
        value={details?.shippingAddress?.addressLine3 || ""}
        onChange={handleShippingChange}
        hideLabel
      />

      <div className="flex gap-4">
        <PaymentInputGroup
          type="text"
          name="locality"
          placeholder="City"
          value={details?.shippingAddress?.locality || ""}
          onChange={handleShippingChange}
          required
        />
        <PaymentInputGroup
          type="text"
          name="administrativeDistrictLevel1"
          placeholder="State"
          value={details?.shippingAddress?.administrativeDistrictLevel1 || ""}
          onChange={handleShippingChange}
          maxLength={2}
          required
        />
        <PaymentInputGroup
          type="text"
          name="postalCode"
          placeholder="Postcode"
          value={details?.shippingAddress?.postalCode || ""}
          onChange={handleShippingChange}
          maxLength={5}
          required
        />
      </div>

      <PaymentSelectGroup
        name="country"
        placeholder="Country"
        value={details?.shippingAddress?.country ?? "US"}
        onChange={handleShippingChange}
        options={[{ value: "US", label: "United States" }]}
        required
      />

      {/* The RECIPIENT's phone, which is why it closes the shipping block
          rather than sitting up with the email — same placement Shopify and
          Amazon use. It reaches the carrier, not us: create-order stamps it on
          the fulfillment recipient and create-printful-order sends it as
          `recipient.phone`. Under the "Contact Information" step header a gift
          buyer would enter their own number and the courier would call the
          wrong person about the delivery.

          Required, not optional: Printful only returns the Express shipping
          rate when the recipient has a phone number, and express carriers need
          one to deliver. See calculate-shipping.js. Validated as a 10-digit US
          number (we ship US-only) so a dropped-digit typo can't reach the
          carrier as an unreachable number or bounce Square's upsert (400
          INVALID_PHONE_NUMBER) as a failed checkout. */}
      <PaymentInputGroup
        type="tel"
        name="phone"
        placeholder="Phone Number"
        value={phone || ""}
        onChange={(e) => {
          setPhoneTouched(true);
          setPhone(e.currentTarget.value);
        }}
        toolTipText="Required — the courier uses it to reach you about your delivery, and expedited shipping rates only appear when it's provided."
        errorText={phoneError}
        required
      />

      {/* Billing Address Checkbox */}
      <div className="my-4 flex flex-col">
        <h2 className="text-lg font-bold">Billing Address</h2>
        <StyledCheckbox
          id="sameAsShipping"
          checked={sameAsShipping}
          onChange={handleSameAsShippingChange}
          label="Same as Shipping Address"
          className="mt-1"
        />
      </div>

      {/* Billing Address (Shown Only If Checkbox is Unchecked) */}
      {!sameAsShipping && (
        <div className="mt-4">
          {/* The cardholder's name, which can differ from the recipient's on a
              gift order. Drives the order-confirmation greeting and the Square
              customer record (PaymentFormPage passes it as givenName/familyName). */}
          <div className="flex gap-4">
            <PaymentInputGroup
              placeholder="First Name"
              type="text"
              name="firstName"
              value={details?.billingAddress?.firstName || ""}
              onChange={handleBillingChange}
              toolTipText="Name on the card — only differs if you're shipping to someone else."
              required
            />
            <PaymentInputGroup
              placeholder="Last Name"
              type="text"
              name="lastName"
              value={details?.billingAddress?.lastName || ""}
              onChange={handleBillingChange}
              required
            />
          </div>
          <AddressSearchInput
            name="addressLine1"
            placeholder="Street Address"
            value={details?.billingAddress?.addressLine1 || ""}
            onChange={handleBillingChange}
            onSelect={handleSelectBillingAddressSearchResult}
            geoapifyKey={geoapifyKey}
            required
          />
          <PaymentInputGroup
            type="text"
            name="addressLine2"
            placeholder="Address Line 2"
            value={details?.billingAddress?.addressLine2 || ""}
            onChange={handleBillingChange}
            hideLabel
          />
          <PaymentInputGroup
            type="text"
            name="addressLine3"
            placeholder="Address Line 3"
            value={details?.billingAddress?.addressLine3 || ""}
            onChange={handleBillingChange}
            hideLabel
          />

          <div className="flex gap-4">
            <PaymentInputGroup
              type="text"
              name="locality"
              placeholder="City"
              value={details?.billingAddress?.locality || ""}
              onChange={handleBillingChange}
              required
            />
            <PaymentInputGroup
              type="text"
              name="administrativeDistrictLevel1"
              placeholder="State"
              value={
                details?.billingAddress?.administrativeDistrictLevel1 || ""
              }
              onChange={handleBillingChange}
              maxLength={2}
              required
            />
            <PaymentInputGroup
              type="text"
              name="postalCode"
              placeholder="Postcode"
              value={details?.billingAddress?.postalCode || ""}
              onChange={handleBillingChange}
              maxLength={5}
              required
            />
          </div>

          <PaymentSelectGroup
            name="country"
            placeholder="Country"
            value={details?.billingAddress?.country ?? "US"}
            onChange={handleBillingChange}
            options={[{ value: "US", label: "United States" }]}
            required
          />
        </div>
      )}
    </>
  );
};

export default PaymentContactInfo;
